<template>
  <v-dialog :value="dialog" max-width="960" scrollable @input="$emit('close')">
    <v-card>
      <v-card-title>
        <v-icon left>mdi-chart-box-outline</v-icon>
        Highlights de Jornada
        <v-spacer />
        <v-btn icon @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle v-if="highlights" class="pb-2">
        {{ highlights.colaborador.nome }} — {{ highlights.periodo.inicio }} a
        {{ highlights.periodo.fim }}
      </v-card-subtitle>

      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <v-card-text v-if="highlights && !loading" class="highlights">
        <h3 class="highlights__titulo">1. Visão geral do período</h3>
        <v-row dense>
          <v-col v-for="item in indicadoresVisao" :key="item.label" cols="6" md="3">
            <div class="highlights__card">
              <div class="highlights__valor">{{ item.valor }}</div>
              <div class="highlights__label">{{ item.label }}</div>
            </div>
          </v-col>
        </v-row>
        <div class="highlights__detalhe">
          Faltas: {{ visao.dias_faltosos }} · Incompletos: {{ visao.dias_incompletos }} · Feriados:
          {{ visao.dias_feriado }} · Férias: {{ visao.dias_ferias }} · Justificados:
          {{ visao.dias_justificados }}
        </div>

        <h3 class="highlights__titulo">2. Padrões de atraso e pontualidade</h3>
        <v-row dense>
          <v-col v-for="item in indicadoresPontualidade" :key="item.label" cols="6" md="3">
            <div class="highlights__card">
              <div class="highlights__valor">{{ item.valor }}</div>
              <div class="highlights__label">{{ item.label }}</div>
            </div>
          </v-col>
        </v-row>
        <v-simple-table v-if="pontualidade.dias.length" dense class="mt-2">
          <thead>
            <tr>
              <th>Data</th>
              <th>Dia da semana</th>
              <th>Entrada prevista</th>
              <th>Entrada registrada</th>
              <th>Atraso</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="dia in pontualidade.dias" :key="dia.data">
              <td>{{ dia.data }}</td>
              <td>{{ dia.dia_semana }}</td>
              <td>{{ dia.entrada_prevista }}</td>
              <td>{{ dia.entrada_registrada }}</td>
              <td>{{ dia.tempo }}</td>
            </tr>
          </tbody>
        </v-simple-table>
        <div v-else class="highlights__vazio">Nenhum atraso acima da tolerância no período.</div>

        <h3 class="highlights__titulo">3. Padrões de saída e horas extras</h3>
        <v-row dense>
          <v-col v-for="item in indicadoresSaida" :key="item.label" cols="6" md="3">
            <div class="highlights__card">
              <div class="highlights__valor">{{ item.valor }}</div>
              <div class="highlights__label">{{ item.label }}</div>
            </div>
          </v-col>
        </v-row>
        <v-simple-table v-if="saidas.saidas_antecipadas.length" dense class="mt-2">
          <thead>
            <tr>
              <th>Data</th>
              <th>Dia da semana</th>
              <th>Saída prevista</th>
              <th>Saída registrada</th>
              <th>Antecipação</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="dia in saidas.saidas_antecipadas" :key="dia.data">
              <td>{{ dia.data }}</td>
              <td>{{ dia.dia_semana }}</td>
              <td>{{ dia.saida_prevista }}</td>
              <td>{{ dia.saida_registrada }}</td>
              <td>{{ dia.tempo }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <div class="highlights__subtitulo">Ocorrências por dia da semana</div>
        <apexchart type="bar" height="240" :options="grafico.options" :series="grafico.series" />

        <h3 class="highlights__titulo">4. Resumo executivo</h3>
        <v-alert outlined color="primary" class="mb-0">
          <p class="mb-2 highlights__resumo">{{ highlights.resumo }}</p>
          <ul v-if="highlights.insights.length" class="mb-0">
            <li v-for="insight in highlights.insights" :key="insight">{{ insight }}</li>
          </ul>
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text color="grey" @click="$emit('close')">Fechar</v-btn>
        <v-btn
          color="red darken-1"
          dark
          :disabled="!highlights || loading"
          @click="$emit('gerar-pdf')"
        >
          <v-icon left small>mdi-file-pdf</v-icon>
          PDF
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const ABREVIACOES = {
  "Segunda-feira": "Seg",
  "Terça-feira": "Ter",
  "Quarta-feira": "Qua",
  "Quinta-feira": "Qui",
  "Sexta-feira": "Sex",
  Sábado: "Sáb",
  Domingo: "Dom",
};

export default {
  name: "EModalHighlightsJornada",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    highlights: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    visao() {
      return this.highlights.visao_geral;
    },
    pontualidade() {
      return this.highlights.pontualidade;
    },
    saidas() {
      return this.highlights.saidas;
    },
    indicadoresVisao() {
      return [
        {
          label: "Dias trabalhados / de expediente",
          valor: `${this.visao.dias_trabalhados} / ${this.visao.dias_devidos}`,
        },
        {
          label: `Horas realizadas (previstas ${this.visao.horas_previstas})`,
          valor: this.visao.horas_realizadas,
        },
        { label: "Saldo do período", valor: this.visao.saldo_periodo },
        { label: "Saldo no banco de horas", valor: this.visao.banco_horas_saldo },
      ];
    },
    indicadoresPontualidade() {
      const percentual = this.pontualidade.percentual_pontualidade;
      return [
        {
          label: "Dias sem atraso",
          valor: percentual === null ? "-" : `${String(percentual).replace(".", ",")}%`,
        },
        { label: "Atrasos acima da tolerância", valor: this.pontualidade.total_atrasos },
        {
          label: `Tempo total (média ${this.pontualidade.media_atraso})`,
          valor: this.pontualidade.tempo_atraso,
        },
        {
          label: "Dia com concentração de atrasos",
          valor: this.pontualidade.dia_mais_frequente?.dia_semana || "—",
        },
      ];
    },
    indicadoresSaida() {
      return [
        { label: "Dias com hora extra", valor: this.saidas.total_dias_hora_extra },
        { label: "Total de horas extras", valor: this.saidas.tempo_horas_extras },
        { label: "Saídas antecipadas", valor: this.saidas.total_saidas_antecipadas },
        { label: "Tempo saído antes do horário", valor: this.saidas.tempo_saidas_antecipadas },
      ];
    },
    grafico() {
      const dias = this.pontualidade.por_dia_semana;
      const ocorrencias = (lista) =>
        dias.map(
          (dia) =>
            lista.find((item) => item.dia_semana_num === dia.dia_semana_num)?.ocorrencias || 0
        );

      return {
        series: [
          { name: "Atrasos", data: dias.map((dia) => dia.ocorrencias) },
          { name: "Horas extras", data: ocorrencias(this.saidas.horas_extras_por_dia_semana) },
          {
            name: "Saídas antecipadas",
            data: ocorrencias(this.saidas.saidas_antecipadas_por_dia_semana),
          },
        ],
        options: {
          chart: {
            toolbar: { show: false },
            background: "transparent",
            foreColor: this.$vuetify.theme.dark ? "#e0e0e0" : "#373d3f",
          },
          theme: { mode: this.$vuetify.theme.dark ? "dark" : "light" },
          grid: { borderColor: this.$vuetify.theme.dark ? "#424242" : "#e0e0e0" },
          colors: ["#e53935", "#43a047", "#fb8c00"],
          plotOptions: { bar: { columnWidth: "55%" } },
          dataLabels: { enabled: false },
          xaxis: { categories: dias.map((dia) => ABREVIACOES[dia.dia_semana] || dia.dia_semana) },
          yaxis: { forceNiceScale: true, labels: { formatter: (valor) => Math.round(valor) } },
          legend: { position: "top" },
          noData: { text: "Sem dias de expediente no período" },
        },
      };
    },
  },
};
</script>

<style scoped>
.highlights__titulo {
  font-size: 1rem;
  font-weight: 600;
  margin: 18px 0 8px;
}

.highlights__titulo:first-child {
  margin-top: 4px;
}

.highlights__subtitulo {
  font-weight: 600;
  margin: 12px 0 4px;
}

/* Cores herdadas do tema (claro ou escuro); textos secundários só ganham opacidade. */
.highlights__card {
  border: 1px solid rgba(128, 128, 128, 0.35);
  border-radius: 6px;
  padding: 10px 12px;
  height: 100%;
}

.highlights__valor {
  font-size: 1.35rem;
  font-weight: 600;
}

.highlights__label {
  font-size: 0.8rem;
  opacity: 0.75;
}

.highlights__detalhe {
  font-size: 0.85rem;
  opacity: 0.75;
  margin-top: 6px;
}

.highlights__vazio {
  opacity: 0.75;
  font-style: italic;
  margin-top: 6px;
}

.highlights__resumo {
  text-align: justify;
}
</style>
